// LibroReclamaciones.jsx - Libro de Reclamaciones Virtual obligatorio en Perú
import React, { useState } from 'react';
import supabase from '../supabaseClientFixed';
import { 
  FileText, User, Mail, Phone, MapPin, AlertCircle, 
  Calendar, Clock, CheckCircle, Send, Building, Hash 
} from 'lucide-react';

const LibroReclamaciones = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: Formulario, 2: Confirmación
  const [loading, setLoading] = useState(false);
  const [reclamoCode, setReclamoCode] = useState('');
  
  // Tipo de documento
  const [tipoDocumento, setTipoDocumento] = useState('DNI');
  
  // Datos del formulario
  const [formData, setFormData] = useState({
    // Datos del consumidor
    nombres: '',
    apellidos: '',
    documento: '',
    email: '',
    telefono: '',
    direccion: '',
    departamento: '',
    provincia: '',
    distrito: '',
    
    // Datos del reclamo
    tipo: 'RECLAMO', // RECLAMO o QUEJA
    descripcion: '',
    pedido: '',
    
    // Datos del bien contratado
    tipoServicio: 'SERVICIO',
    montoReclamado: '',
    descripcionServicio: 'Suscripción MediExamPro - Plataforma de preparación médica'
  });

  // Departamentos del Perú
  const departamentos = [
    'Amazonas', 'Áncash', 'Apurímac', 'Arequipa', 'Ayacucho', 'Cajamarca',
    'Callao', 'Cusco', 'Huancavelica', 'Huánuco', 'Ica', 'Junín',
    'La Libertad', 'Lambayeque', 'Lima', 'Loreto', 'Madre de Dios',
    'Moquegua', 'Pasco', 'Piura', 'Puno', 'San Martín', 'Tacna',
    'Tumbes', 'Ucayali'
  ];

  // Generar código único de reclamo
  const generateReclamoCode = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `REC-${year}${month}${day}-${random}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const required = [
      'nombres', 'apellidos', 'documento', 'email', 'telefono',
      'direccion', 'departamento', 'provincia', 'distrito',
      'descripcion', 'pedido'
    ];
    
    for (let field of required) {
      if (!formData[field]?.trim()) {
        alert(`Por favor complete el campo: ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Por favor ingrese un email válido');
      return false;
    }
    
    // Validar documento según tipo
    if (tipoDocumento === 'DNI' && formData.documento.length !== 8) {
      alert('El DNI debe tener 8 dígitos');
      return false;
    }
    if (tipoDocumento === 'CE' && formData.documento.length < 9) {
      alert('El Carné de Extranjería debe tener al menos 9 caracteres');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const code = generateReclamoCode();
      const fecha = new Date().toISOString();
      
      // Guardar en Supabase
      const { error: dbError } = await supabase
        .from('libro_reclamaciones')
        .insert([{
          codigo: code,
          fecha_reclamo: fecha,
          tipo_documento: tipoDocumento,
          numero_documento: formData.documento,
          nombres: formData.nombres,
          apellidos: formData.apellidos,
          email: formData.email,
          telefono: formData.telefono,
          direccion: formData.direccion,
          departamento: formData.departamento,
          provincia: formData.provincia,
          distrito: formData.distrito,
          tipo_reclamo: formData.tipo,
          descripcion: formData.descripcion,
          pedido: formData.pedido,
          tipo_servicio: formData.tipoServicio,
          monto_reclamado: formData.montoReclamado || '0',
          descripcion_servicio: formData.descripcionServicio,
          estado: 'PENDIENTE',
          ruc_empresa: '10709315302'
        }]);
      
      if (dbError) {
        console.error('Error guardando reclamo:', dbError);
        // Si la tabla no existe, continuar de todos modos
      }
      
      // Enviar email al consumidor
      try {
        await fetch('/.netlify/functions/send-complaint-confirmation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            codigo: code,
            nombre: formData.nombres,
            apellidos: formData.apellidos,
            tipo: formData.tipo.toLowerCase(),
            descripcion: formData.descripcion,
            pedido: formData.pedido,
            bien_contratado: formData.descripcionServicio,
            monto_reclamado: formData.montoReclamado || '0',
            telefono: formData.telefono,
            tipo_documento: tipoDocumento,
            numero_documento: formData.documento
          })
        });
      } catch (emailError) {
        console.log('No se pudo enviar email de confirmación');
      }
      
      setReclamoCode(code);
      setStep(2);
      
    } catch (error) {
      console.error('Error:', error);
      alert('Hubo un error al procesar su reclamo. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleNewReclamo = () => {
    setStep(1);
    setFormData({
      nombres: '',
      apellidos: '',
      documento: '',
      email: '',
      telefono: '',
      direccion: '',
      departamento: '',
      provincia: '',
      distrito: '',
      tipo: 'RECLAMO',
      descripcion: '',
      pedido: '',
      tipoServicio: 'SERVICIO',
      montoReclamado: '',
      descripcionServicio: 'Suscripción MediExamPro - Plataforma de preparación médica'
    });
    setReclamoCode('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full">
          {/* Header Corporativo */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-t-xl">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="h-8 w-8" />
                  <h1 className="text-2xl font-bold">LIBRO DE RECLAMACIONES</h1>
                </div>
                <p className="text-blue-100 text-sm">
                  Conforme al Código de Protección y Defensa del Consumidor
                </p>
                <p className="text-blue-100 text-xs mt-1">
                  RUC: 10709315302 | MEDIEXAMPRO
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Contenido */}
          {step === 1 ? (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Sección 1: Identificación del Consumidor */}
              <div className="border-b pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  1. IDENTIFICACIÓN DEL CONSUMIDOR RECLAMANTE
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombres <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nombres"
                      value={formData.nombres}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellidos <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="apellidos"
                      value={formData.apellidos}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Documento <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={tipoDocumento}
                      onChange={(e) => setTipoDocumento(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="DNI">DNI</option>
                      <option value="CE">Carné de Extranjería</option>
                      <option value="PASAPORTE">Pasaporte</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número de Documento <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="documento"
                      value={formData.documento}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dirección <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Departamento <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="departamento"
                      value={formData.departamento}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Seleccione...</option>
                      {departamentos.map(dep => (
                        <option key={dep} value={dep}>{dep}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Provincia <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="provincia"
                      value={formData.provincia}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Distrito <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="distrito"
                      value={formData.distrito}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Sección 2: Identificación del Bien Contratado */}
              <div className="border-b pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  2. IDENTIFICACIÓN DEL BIEN CONTRATADO
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo
                    </label>
                    <select
                      name="tipoServicio"
                      value={formData.tipoServicio}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="SERVICIO">Servicio</option>
                      <option value="PRODUCTO">Producto</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monto Reclamado (S/.)
                    </label>
                    <input
                      type="number"
                      name="montoReclamado"
                      value={formData.montoReclamado}
                      onChange={handleInputChange}
                      placeholder="39.90"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descripción
                    </label>
                    <input
                      type="text"
                      name="descripcionServicio"
                      value={formData.descripcionServicio}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Sección 3: Detalle de la Reclamación */}
              <div className="border-b pb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-600" />
                  3. DETALLE DE LA RECLAMACIÓN Y PEDIDO DEL CONSUMIDOR
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="tipo"
                          value="RECLAMO"
                          checked={formData.tipo === 'RECLAMO'}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">
                          <strong>RECLAMO</strong>
                          <span className="text-gray-500 block text-xs">
                            Disconformidad con el servicio
                          </span>
                        </span>
                      </label>
                      
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="tipo"
                          value="QUEJA"
                          checked={formData.tipo === 'QUEJA'}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">
                          <strong>QUEJA</strong>
                          <span className="text-gray-500 block text-xs">
                            Malestar por atención o conducta
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Detalle <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="descripcion"
                      value={formData.descripcion}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describa detalladamente su reclamo o queja..."
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pedido <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="pedido"
                      value={formData.pedido}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="¿Qué solución espera de nosotros?"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Importante:</strong> La formulación del reclamo no impide acudir a otras vías de solución de controversias ni es requisito previo para interponer una denuncia ante el INDECOPI.
                </p>
                <p className="text-sm text-blue-800 mt-2">
                  El proveedor debe dar respuesta al reclamo o queja en un plazo no mayor a quince (15) días hábiles.
                </p>
              </div>

              {/* Botones */}
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Enviar Reclamo
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            // Step 2: Confirmación
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  ¡Reclamo Registrado Exitosamente!
                </h2>
                <p className="text-gray-600">
                  Su reclamo ha sido registrado y se le ha enviado una copia a su correo electrónico.
                </p>
              </div>

              {/* Hoja de Reclamación */}
              <div className="border-2 border-gray-300 rounded-lg p-6 bg-white" id="hoja-reclamacion">
                <div className="text-center mb-4 border-b pb-4">
                  <h3 className="text-xl font-bold">HOJA DE RECLAMACIÓN</h3>
                  <p className="text-sm text-gray-600">N° {reclamoCode}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Fecha: {new Date().toLocaleString('es-PE')}
                  </p>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-semibold">Consumidor:</span>
                      <p>{formData.nombres} {formData.apellidos}</p>
                    </div>
                    <div>
                      <span className="font-semibold">Documento:</span>
                      <p>{tipoDocumento}: {formData.documento}</p>
                    </div>
                    <div>
                      <span className="font-semibold">Email:</span>
                      <p>{formData.email}</p>
                    </div>
                    <div>
                      <span className="font-semibold">Teléfono:</span>
                      <p>{formData.telefono}</p>
                    </div>
                  </div>

                  <div>
                    <span className="font-semibold">Dirección:</span>
                    <p>{formData.direccion}, {formData.distrito}, {formData.provincia}, {formData.departamento}</p>
                  </div>

                  <div className="border-t pt-3">
                    <span className="font-semibold">Tipo:</span> {formData.tipo}
                  </div>

                  <div>
                    <span className="font-semibold">Descripción:</span>
                    <p className="mt-1">{formData.descripcion}</p>
                  </div>

                  <div>
                    <span className="font-semibold">Pedido:</span>
                    <p className="mt-1">{formData.pedido}</p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t text-center text-xs text-gray-500">
                  <p>MediExamPro | RUC: 10709315302</p>
                  <p>Este documento tiene carácter de Declaración Jurada</p>
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex justify-between mt-6">
                <button
                  onClick={handleNewReclamo}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Nuevo Reclamo
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={handlePrint}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Imprimir
                  </button>
                  
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LibroReclamaciones;
